import { useState, useEffect } from 'react';
import AdminReviewService from '../../services/AdminReviewService';
import { fetchBoats } from '../../services/AdminBoatService';

const useReviewActions = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [boats, setBoats] = useState([]);

  useEffect(() => {
    loadReviews();
    loadBoats();
  }, []);

  useEffect(() => {
    filterReviews();
  }, [reviews, searchTerm]);

  const loadReviews = async () => {
    try {
      setIsLoading(true);
      const reviewsData = await AdminReviewService.fetchReviews();
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBoats = async () => {
    try {
      const boatsData = await fetchBoats();
      setBoats(boatsData);
    } catch (error) {
      console.error('Error loading boats:', error);
    }
  };

  const filterReviews = () => {
    let filtered = reviews;

    if (searchTerm) {
      filtered = reviews.filter(review =>
        review.reviewer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.boat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredReviews(filtered);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedReviews(filteredReviews.map(review => review.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleReviewSelect = (reviewId) => {
    setSelectedReviews(prev => {
      if (prev.includes(reviewId)) {
        return prev.filter(id => id !== reviewId);
      } else {
        return [...prev, reviewId];
      }
    });
  };

  const clearSelectedReviews = () => {
    setSelectedReviews([]);
  };

  const handleBulkApprove = async () => {
    if (selectedReviews.length === 0) return;

    try {
      await Promise.all(selectedReviews.map(id => AdminReviewService.approveReview(id)));
      await loadReviews();
      setSelectedReviews([]);
    } catch (error) {
      console.error('Error bulk approving reviews:', error);
    }
  };

  const handleBulkReject = async () => {
    if (selectedReviews.length === 0) return;

    try {
      await Promise.all(selectedReviews.map(id => AdminReviewService.rejectReview(id)));
      await loadReviews();
      setSelectedReviews([]);
    } catch (error) {
      console.error('Error bulk rejecting reviews:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedReviews.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedReviews.length} review(s)?`)) {
      try {
        await Promise.all(selectedReviews.map(id => AdminReviewService.deleteReview(id)));
        await loadReviews();
        setSelectedReviews([]);
      } catch (error) {
        console.error('Error bulk deleting reviews:', error);
      }
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      await AdminReviewService.approveReview(reviewId);
      await loadReviews();
    } catch (error) {
      console.error('Error approving review:', error);
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      await AdminReviewService.rejectReview(reviewId);
      await loadReviews();
    } catch (error) {
      console.error('Error rejecting review:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await AdminReviewService.deleteReview(reviewId);
        await loadReviews();
      } catch (error) {
        console.error('Error deleting review:', error);
      }
    }
  };

  return {
    reviews: filteredReviews,
    selectedReviews,
    searchTerm,
    isLoading,
    boats,
    handleSearch,
    handleSelectAll,
    handleReviewSelect,
    clearSelectedReviews,
    handleBulkApprove,
    handleBulkReject,
    handleBulkDelete,
    handleApproveReview,
    handleRejectReview,
    handleDeleteReview,
    loadReviews
  };
};

export default useReviewActions; 